"use client";

import { ArrowUpDown, X } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LISTING_SORTS,
  type FilterOption,
  type ListingFilterOptions,
  type ListingFilters,
  type ListingSort,
  type BrowserListing,
} from "./listing-filters";
import { ListingFiltersDialog } from "./listing-filters-dialog";

function sortLabel(sort: ListingSort, hasDistance: boolean): string {
  switch (sort) {
    case "default":
      return hasDistance ? "Nearest" : "Recommended";
    case "price-asc":
      return "Price: low to high";
    case "price-desc":
      return "Price: high to low";
  }
}

function SortMenu({
  sort,
  hasDistance,
  onChange,
}: {
  sort: ListingSort;
  hasDistance: boolean;
  onChange: (sort: ListingSort) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="chip" size="chip" aria-label={`Sort by ${sortLabel(sort, hasDistance)}`}>
          <ArrowUpDown aria-hidden />
          <span className="hidden sm:inline">{sortLabel(sort, hasDistance)}</span>
          <span className="sm:hidden">Sort</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup
          value={sort}
          onValueChange={(value) => {
            const next = LISTING_SORTS.find((option) => option === value);
            if (next) onChange(next);
          }}
        >
          {LISTING_SORTS.map((option) => (
            <DropdownMenuRadioItem key={option} value={option}>
              {sortLabel(option, hasDistance)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type AppliedFilter = { key: string; label: string; remove: (filters: ListingFilters) => ListingFilters };

function without(values: readonly string[], value: string): string[] {
  return values.filter((entry) => entry !== value);
}

function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

function priceLabel(min: number | null, max: number | null): string {
  if (min !== null && max !== null) return `${dollars(min)} to ${dollars(max)}`;
  return min !== null ? `${dollars(min)} or more` : `Up to ${dollars(max ?? 0)}`;
}

function labelFor(options: readonly FilterOption[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

function appliedFilters(filters: ListingFilters, options: ListingFilterOptions): AppliedFilter[] {
  const applied: AppliedFilter[] = [];
  if (filters.query) {
    applied.push({ key: "query", label: `\u201c${filters.query}\u201d`, remove: (next) => ({ ...next, query: "" }) });
  }
  if (filters.maxDistanceMiles !== null) {
    applied.push({
      key: "distance",
      label: `Within ${filters.maxDistanceMiles} miles`,
      remove: (next) => ({ ...next, maxDistanceMiles: null }),
    });
  }
  for (const value of filters.conditions) {
    applied.push({
      key: `condition-${value}`,
      label: labelFor(options.conditions, value),
      remove: (next) => ({ ...next, conditions: without(next.conditions, value) }),
    });
  }
  for (const value of filters.sizes) {
    applied.push({
      key: `size-${value}`,
      label: labelFor(options.sizes, value),
      remove: (next) => ({ ...next, sizes: without(next.sizes, value) }),
    });
  }
  if (filters.minPriceCents !== null || filters.maxPriceCents !== null) {
    applied.push({
      key: "price",
      label: priceLabel(filters.minPriceCents, filters.maxPriceCents),
      remove: (next) => ({ ...next, minPriceCents: null, maxPriceCents: null }),
    });
  }
  return applied;
}

function AppliedFilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <Button
      variant="chip"
      size="chip"
      className="border-transparent bg-action-tint pr-3 hover:bg-action-tint hover:border-action/30"
      aria-label={`Remove filter: ${label}`}
      onClick={onRemove}
    >
      {label}
      <X aria-hidden className="text-fg-muted" />
    </Button>
  );
}

type ListingBrowserControlsProps = {
  cards: readonly BrowserListing[] | null;
  listingsFailed: boolean;
  options: ListingFilterOptions;
  filters: ListingFilters;
  hasDistance: boolean;
  onFiltersChange: (filters: ListingFilters) => void;
  onClearAll: () => void;
};

export function listingCountLabel(resultCount: number, total: number): string {
  return resultCount === total
    ? `${total} ${total === 1 ? "listing" : "listings"}`
    : `${resultCount} of ${total} listings`;
}

export function ListingBrowserControls({
  cards,
  listingsFailed,
  options,
  filters,
  hasDistance,
  onFiltersChange,
  onClearAll,
}: ListingBrowserControlsProps) {
  const applied = appliedFilters(filters, options);
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {applied.map((filter) => (
        <AppliedFilterChip key={filter.key} label={filter.label} onRemove={() => onFiltersChange(filter.remove(filters))} />
      ))}
      <SortMenu sort={filters.sort} hasDistance={hasDistance} onChange={(sort) => onFiltersChange({ ...filters, sort })} />
      <ListingFiltersDialog
        cards={cards}
        listingsFailed={listingsFailed}
        options={options}
        filters={filters}
        hasDistance={hasDistance}
        onApply={onFiltersChange}
      />
      {applied.length > 1 ? (
        <Button variant="link" className="min-h-11 px-2 text-[0.9375rem]" onClick={onClearAll}>
          Clear all
        </Button>
      ) : null}
    </div>
  );
}
